<template>
    <div class="liquidations-table">
        <!-- NEW BUTTON AND SEARCH -->
        <b-row v-if="showSearch" class="mb-2 d-flex flex-wrap justify-content-end">
            <b-col v-if="showSearch" cols="9" md="auto" class="mb-1 mb-md-0 justify-content-end">
                <b-form-input id="filterInput" v-model="filter" type="search" :placeholder="`${$t('Cerca')}...`"
                    :disabled="loading" />
            </b-col>
        </b-row>

        <!-- TABLE -->
        <b-row>
            <b-col md="12">
                <b-table striped borderless hover responsive class="position-relative" :busy="loading"
                    :per-page="perPage" :current-page="currentPage" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
                    sort-icon-left :items="filteredItems" show-empty :empty-text="$t('errors.emptyTable.title')"
                    :empty-filtered-text="$t('errors.emptyTable.title')" :fields="fields" :filter="filter"
                    @filtered="onFiltered" @row-clicked="onRowClicked" @row-contextmenu="onRowRightClicked"
                    @row-middle-clicked="onRowMiddleClicked">

                    <template #table-busy>
                        <div class="text-center text-primary my-2">
                            <b-spinner class="align-middle mr-2" />
                            <strong>{{ $t("Carregant") }}...</strong>
                        </div>
                    </template>

                    <!-- Column -->
                    <template #cell(emittedAt)="data">
                        {{ formatDateStringToDate(data.value) }}
                    </template>

                    <!-- Column -->
                    <template #cell(payedAt)="data">
                        {{ formatDateStringToDate(data.value) }}
                    </template>

                    <!-- Column -->
                    <template #cell(pvpPrice)="data">
                        {{ formatCurrency(data.value) }}
                    </template>

                    <!-- Column -->
                    <template #cell(raw)="data">
                        <pre>{{ data.item }}</pre>
                    </template>
                </b-table>
            </b-col>
        </b-row>

        <!-- PAGINATION -->
        <b-row v-show="!loading" v-if="totalRows > pageOptions[0]"
            class="d-flex justify-content-between align-items-center">
            <b-col cols="12" md="4" lg="3" class="mb-1 mb-md-0 d-flex justify-content-center justify-content-md-start">
                <b-form-group id="per-page-input" :label="`${$t('Per pàgina')}`" label-cols="6" label-align="left"
                    label-size="sm" label-for="sortBySelect" class="text-nowrap mr-1">
                    <b-form-select id="perPageSelect" v-model="perPage" size="sm" inline :options="pageOptions" />
                </b-form-group>
            </b-col>
            <b-col cols="12" md="4" lg="3" class="mb-1 mb-md-0 d-flex justify-content-center justify-content-md-end">
                <div>
                    <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" first-number
                        last-number prev-class="prev-item" next-class="next-item">
                        <template #prev-text>
                            <feather-icon icon="ChevronLeftIcon" size="18" />
                        </template>
                        <template #next-text>
                            <feather-icon icon="ChevronRightIcon" size="18" />
                        </template>
                    </b-pagination>
                </div>
            </b-col>
        </b-row>
    </div>
</template>

<script>
/**
 * LiquidationsTable component
 * Displays a table of liquidations with filtering, sorting, and pagination.
 *
 * @component
 * @prop {Array} items - Array of liquidation objects to display.
 * @prop {Boolean} loading - Whether the table is in a loading state.
 * @prop {Boolean} showSearch - Whether to show the search input.
 * @prop {Boolean} hideEmittedAt - Hide the "emittedAt" column if true.
 * @prop {Boolean} hidePayedAt - Hide the "payedAt" column if true.
 * @prop {Boolean} hidePvpPrice - Hide the "pvpPrice" column if true.
 */

import { formatCurrency, formatDateStringToDate } from "@/utils/formatters";
import {
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BPagination,
    BFormInput,
    BSpinner,
    // BButton,
    // BBadge,
    // BFormCheckbox,
} from "bootstrap-vue";
// import BAccommodation from "@foravila-core/components/b-accommodation/BAccommodation.vue";
// import BStatusBadge from "@foravila-core/components/b-status-badge/BStatusBadge.vue";
// import BBookingLocalizator from "@foravila-core/components/b-booking-localizator/BBookingLocalizator.vue";
// import BClient from "@foravila-core/components/b-client/BClient.vue";

export default {
    name: "ContractsTable",
    components: {
        BTable,
        BRow,
        BCol,
        BFormGroup,
        BFormSelect,
        BPagination,
        BFormInput,
        BSpinner,
        // BButton,
        // BBadge,
        // BAccommodation,
        // BStatusBadge,
        // BBookingLocalizator,
        // BClient,
        // BFormCheckbox,
    },
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: () => false,
        },
        showSearch: {
            type: Boolean,
            default: () => false,
        },
        hideEmittedAt: {
            type: Boolean,
            default: () => false,
        },
        hidePayedAt: {
            type: Boolean,
            default: () => false,
        },
        hidePvpPrice: {
            type: Boolean,
            default: () => false,
        },
    },
    data() {
        return {
            isBusy: false,
            sortBy: "sortStartDate",
            sortDesc: true,
            perPage: 10,
            pageOptions: [5, 10, 50, 100, 500],
            totalRows: 1,
            currentPage: 1,
            filter: null,
            fields: [],
            selectedItem: null,
            formatCurrency,
            formatDateStringToDate,
        };
    },
    computed: {
        filteredItems() {
            // TODO: Add more advanced filtering if needed
            return this.items;
        },
    },
    watch: {
        filteredItems(items) {
            this.totalRows = items.length;
        },
    },
    created() {
        this.setTableFields();
    },
    mounted() {
        // Set the initial number of items
        this.totalRows = this.filteredItems.length;
    },
    methods: {
        setTableFields() {
            if (!this.hideEmittedAt) {
                this.fields.push({ key: "emittedAt", label: this.$t("Data emissió"), sortable: true, thStyle: { width: "150px" } });
            }
            if (!this.hidePayedAt) {
                this.fields.push({ key: "payedAt", label: this.$t("Data pagament"), sortable: true, thStyle: { width: "150px" } });
            }
            if (!this.hidePvpPrice) {
                this.fields.push({ key: "pvpPrice", label: this.$t("Preu PVP"), sortable: true });
            }
            // TODO: remove after debugging is done
            this.fields.push({ key: "raw", label: "Raw Data", sortable: false });
        },
        onFiltered(filteredItems) {
            // Trigger pagination to update the number of buttons/pages due to filtering
            this.totalRows = filteredItems.length;
            this.currentPage = 1;
        },
        onRowClicked(item) {
            // this.$router.push({
            //     name: "foravila-contract-view",
            //     params: {
            //         contractUuid: item.uuid,
            //     },
            // });
        },
        onRowRightClicked(item) {
            this.selectedItem = item;
            this.openInNewTab();
        },
        onRowMiddleClicked(item) {
            this.selectedItem = item;
            this.openInNewTab();
        },
        openInNewTab() {
            // const routeData = this.$router.resolve({
            //     name: "foravila-contract-view",
            //     params: {
            //         contractUuid: this.selectedItem.uuid,
            //     },
            // });
            // window.open(routeData.href, "_blank");
        },
    },
};
</script>

<style lang="scss">
.liquidations-table {
    #per-page-input__BV_label_ {
        margin-top: 5px;
    }
}
</style>
